// Shopify Storefront API (GraphQL) client — public read-only access.
// Runs entirely in the browser. Do NOT put admin tokens here.

const DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN || "v1hj1w-3k.myshopify.com";
const VERSION = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_VERSION || "2025-01";
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN || "";

export const ENDPOINT = DOMAIN
    ? `https://${DOMAIN}/api/${VERSION}/graphql.json`
    : null;

export const isShopifyConfigured = () => Boolean(DOMAIN && TOKEN && ENDPOINT);

async function storefront(query, variables = {}) {
    if (!isShopifyConfigured()) {
        throw new Error("Shopify Storefront API is not configured.");
    }
    const authHeaders = TOKEN.startsWith("shpat_")
        ? { "Shopify-Storefront-Private-Token": TOKEN }
        : { "X-Shopify-Storefront-Access-Token": TOKEN };

    const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            ...authHeaders,
        },
        body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) {
        throw new Error(`Shopify request failed (${res.status})`);
    }
    const json = await res.json();
    if (json.errors && json.errors.length) {
        throw new Error(json.errors.map((e) => e.message).join("; "));
    }
    return json.data;
}

// Slug matchers used to route products to our category cards based on productType
const CATEGORY_MATCHERS = [
    { slug: "custom-and-accessories", test: (t) => /custom|bespoke|accessor|hardware|canopy|dimmer|prism|droplet/.test(t) },
    { slug: "kids-lights", test: (t) => /\bkids?\b|children|nursery|animal/.test(t) },
    { slug: "mirror-lights", test: (t) => /mirror/.test(t) },
    { slug: "fan-chandeliers", test: (t) => /\bfan\b/.test(t) && /chandelier/.test(t) },
    { slug: "double-height-chandeliers", test: (t) => /double\s*height/.test(t) },
    { slug: "chandeliers", test: (t) => /chandelier/.test(t) },
    { slug: "pendants", test: (t) => /pendant/.test(t) },
    { slug: "wall", test: (t) => /\bwall\b/.test(t) || /sconce/.test(t) },
    { slug: "table", test: (t) => /table\s*lamp|desk\s*lamp/.test(t) },
    { slug: "floor", test: (t) => /floor\s*lamp/.test(t) },
    { slug: "ceiling", test: (t) => /ceiling|flush/.test(t) },
    { slug: "outdoor", test: (t) => /outdoor|exterior|bollard|landscape|gate/.test(t) },
    { slug: "bamboo", test: (t) => /bamboo|rattan|wicker/.test(t) },
    { slug: "bulbs", test: (t) => /\bbulb\b|filament|edison/.test(t) },
];

export function categoryForProductType(productType) {
    const t = (productType || "").toLowerCase();
    if (!t) return "";
    const hit = CATEGORY_MATCHERS.find((c) => c.test(t));
    return hit ? hit.slug : "";
}

function subcategoryForProductType(productType) {
    const t = (productType || "").toLowerCase();
    if (/\bcustom\b|\bbespoke\b/.test(t)) return "custom";
    if (/\baccessor|\bhardware|\bcanopy|\bdimmer|\bpart\b|\bprism\b/.test(t)) return "accessories";
    if (/\bclassic\b/.test(t)) return "classic";
    if (/\bmodern\b/.test(t)) return "modern";
    if (/\bgate\b/.test(t)) return "gate-lights";
    if (/\bwall\b/.test(t)) return "wall-lamps";
    return "";
}

export function collectionImageForCategory(cat) {
    switch (cat) {
        case "custom-and-accessories": return "/collections/custom-and-accessories.jpg";
        case "chandeliers": return "/collections/page11.jpg";
        case "double-height-chandeliers": return "/collections/page9.jpg";
        case "fan-chandeliers": return "/collections/page6.jpg";
        case "pendants": return "/collections/page8.jpg";
        case "wall": return "/collections/page10.jpg";
        case "table": return "/collections/page1.jpg";
        case "floor": return "/collections/page3.jpg";
        case "ceiling": return "/collections/page4.jpg";
        case "outdoor": return "/collections/page7.jpg";
        case "bamboo": return "/collections/page2.jpg";
        case "kids-lights": return "/collections/kids-lights.png";
        case "mirror-lights": return "/collections/mirror-lights.jpg";
        case "bulbs": return "/collections/page5.jpg";
        default: return "/collections/page11.jpg";
    }
}

export function normalizeProduct(p) {
    if (!p) return null;
    const price = Math.round(Number(p.priceRange?.minVariantPrice?.amount || 0));
    const compareAt = p.compareAtPriceRange?.minVariantPrice?.amount
        ? Math.round(Number(p.compareAtPriceRange.minVariantPrice.amount))
        : null;
    let images = (p.images?.nodes || []).map((n) => n.url).filter(Boolean);
    if (images.length === 0 && p.featuredImage?.url) images.push(p.featuredImage.url);

    // Dedicated photography for products without uploaded images on Shopify
    if (images.length === 0) {
        if (p.handle === "draped-crystal-chain-chandelier") {
            images = ["/collections/draped-crystal-chain-chandelier.jpg"];
        } else if (p.handle === "twisted-crystal-floor-lamp") {
            images = ["/collections/twisted-crystal-floor-lamp.jpg"];
        }
    }

    const tags = (p.tags || []).map((t) => t.toLowerCase());
    const collectionHandles = (p.collections?.nodes || []).map((c) => c.handle);
    const category =
        categoryForProductType(p.productType) ||
        collectionHandles.find((h) => h !== "frontpage" && h !== "all") ||
        "";
    const subcategory = subcategoryForProductType(p.productType) ||
        (tags.includes("classic") ? "classic" : tags.includes("modern") ? "modern" : "");

    return {
        id: p.id,
        slug: p.handle,
        handle: p.handle,
        name: p.title,
        productType: p.productType || "",
        price,
        compareAtPrice: compareAt,
        description: p.description || "",
        descriptionHtml: p.descriptionHtml || p.description || "",
        images,
        tags,
        collectionHandles,
        category,
        subcategory,
        featured: tags.includes("featured") || true,
        new_arrival: tags.includes("new-arrival") || tags.includes("new"),
        availableForSale: p.availableForSale ?? true,
        variants: (p.variants?.nodes || []).map((v) => ({
            id: v.id,
            title: v.title,
            price: Math.round(Number(v.price.amount)),
            availableForSale: v.availableForSale,
            selectedOptions: v.selectedOptions || [],
            image: v.image?.url || images[0] || "",
        })),
        firstVariantId: p.variants?.nodes?.[0]?.id,
    };
}

const PRODUCT_FIELDS = `
    id
    handle
    title
    description
    descriptionHtml
    productType
    tags
    availableForSale
    featuredImage { url altText }
    images(first: 10) { nodes { url altText } }
    priceRange { minVariantPrice { amount currencyCode } }
    compareAtPriceRange { minVariantPrice { amount currencyCode } }
    collections(first: 5) { nodes { handle title } }
    variants(first: 25) {
        nodes {
            id
            title
            availableForSale
            price { amount currencyCode }
            selectedOptions { name value }
            image { url altText }
        }
    }
`;

const Q_PRODUCTS = `
    query Products($first: Int!, $query: String) {
        products(first: $first, query: $query) {
            nodes { ${PRODUCT_FIELDS} }
        }
    }
`;

const Q_PRODUCT_BY_HANDLE = `
    query Product($handle: String!) {
        product(handle: $handle) { ${PRODUCT_FIELDS} }
    }
`;

// Built-in catalog with Authentic Products & Dedicated Photography
const FALLBACK_PRODUCTS = [
    {
        "id": "prod-bespoke-grand-cascade",
        "slug": "bespoke-grand-cascade-chandelier",
        "handle": "bespoke-grand-cascade-chandelier",
        "name": "Bespoke Grand Cascade Chandelier",
        "productType": "Custom Lighting",
        "category": "custom-and-accessories",
        "subcategory": "custom",
        "price": 285000,
        "compareAtPrice": 320000,
        "description": "An architectural made-to-order waterfall chandelier crafted with tiers of fluted crystal glass and hand-brushed antique brass armature. Tailored precisely to your ceiling height and canopy layout.",
        "descriptionHtml": "<p><strong>Bespoke Grand Cascade Chandelier</strong></p><p>An architectural made-to-order waterfall chandelier crafted with tiers of fluted crystal glass and hand-brushed antique brass armature. Each fixture is individually commissioned and tailored to your specific foyer, dining room, or double-height stairwell.</p><p><strong>Customization Options:</strong></p><ul><li>Diameter: Custom sizes from 800mm to 2200mm</li><li>Suspension drop: 1.2m to 6.0m tailored to your ceiling</li><li>Metal finishes: Antique Brass, Brushed Champagne Gold, or Matte Bronze</li><li>Light source: Premium warm 3000K LED array with 0-10V or TRIAC dimming</li></ul><p>Includes dedicated CAD drawing review and architect installation support.</p>",
        "finish": "Antique Brass / Custom Patina",
        "dimensions": "Made to Measure (Up to 6000mm Drop)",
        "material": "Hand-blown fluted crystal glass, solid brass armature",
        "images": [
            "/collections/custom-chandelier.jpg"
        ],
        "tags": [
            "custom",
            "bespoke",
            "custom-and-accessories",
            "chandelier"
        ],
        "featured": true,
        "new_arrival": true,
        "availableForSale": true,
        "variants": [
            {
                "id": "var-custom-cascade-1",
                "title": "Custom Commission / Tailored Spec",
                "price": 285000,
                "availableForSale": true
            }
        ],
        "firstVariantId": "var-custom-cascade-1"
    },
    {
        "id": "prod-bespoke-architectural-sculptural",
        "slug": "bespoke-architectural-sculptural-chandelier",
        "handle": "bespoke-architectural-sculptural-chandelier",
        "name": "Bespoke Architectural Sculptural Chandelier",
        "productType": "Custom Lighting",
        "category": "custom-and-accessories",
        "subcategory": "custom",
        "price": 340000,
        "compareAtPrice": 390000,
        "description": "A statement geometric brass chandelier combining bold structural framing with hand-blown ribbed amber glass lanterns. Handcrafted in our studio to create a mesmerizing visual anchor in expansive residential and commercial interiors.",
        "descriptionHtml": "<p><strong>Bespoke Architectural Sculptural Chandelier</strong></p><p>A statement geometric brass chandelier combining bold structural framing with hand-blown ribbed amber glass lanterns. Handcrafted in our studio to create a mesmerizing visual anchor in expansive residential and commercial interiors.</p><p><strong>Customization Details:</strong></p><ul><li>Framework: Hand-forged architectural brass with patina finish</li><li>Diffusers: Fluted amber and clear crystal glass capsules</li><li>Dimensions: Configurable from 1200mm to 2400mm length</li><li>Light source: Warm dimmable LED capsules (2700K - 3000K)</li></ul><p>Our bespoke design team provides personalized 3D renderings before production.</p>",
        "finish": "Brushed Architectural Bronze",
        "dimensions": "L 1600mm × W 850mm × H 950mm (Customizable)",
        "material": "Forged brass, fluted amber art glass",
        "images": [
            "/collections/custom-sculptural-chandelier.jpg"
        ],
        "tags": [
            "custom",
            "bespoke",
            "custom-and-accessories"
        ],
        "featured": true,
        "new_arrival": true,
        "availableForSale": true,
        "variants": [
            {
                "id": "var-custom-sculptural-1",
                "title": "Bespoke Installation Spec",
                "price": 340000,
                "availableForSale": true
            }
        ],
        "firstVariantId": "var-custom-sculptural-1"
    },
    {
        "id": "prod-solid-brass-canopy-downrod-kit",
        "slug": "solid-brass-canopy-downrod-kit",
        "handle": "solid-brass-canopy-downrod-kit",
        "name": "Solid Brass Ceiling Canopy & Downrod Kit",
        "productType": "Lighting Accessories",
        "category": "custom-and-accessories",
        "subcategory": "accessories",
        "price": 4800,
        "compareAtPrice": 5800,
        "description": "Heavy-gauge solid brass ceiling canopy, extension downrod and swivel mounting hardware. Compatible with all Adore pendant and chandelier suspensions.",
        "descriptionHtml": "<p><strong>Solid Brass Ceiling Canopy & Downrod Kit</strong></p><p>A precision-machined solid brass ceiling rose and extension rod designed for high ceilings, sloped ceilings, and retrofit installations.</p><p><strong>Specifications:</strong></p><ul><li>Material: Solid spun brass with clear anti-tarnish protective coating</li><li>Canopy Diameter: 140 mm | Depth: 35 mm</li><li>Includes 2 × 500 mm downrods (interlocking for up to 1000 mm drop)</li><li>Swivel ball joint suitable for flat and pitched/sloped ceilings up to 45°</li><li>Includes heavy-duty mounting bracket and brass hardware</li></ul>",
        "finish": "Brushed Solid Brass",
        "dimensions": "Ø 140mm canopy, 1000mm downrods",
        "material": "Solid brass",
        "images": [
            "/collections/brass-canopy-kit.jpg"
        ],
        "tags": [
            "accessories",
            "hardware",
            "canopy",
            "custom-and-accessories"
        ],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "var-acc-canopy-brass",
                "title": "Solid Brushed Brass",
                "price": 4800,
                "availableForSale": true
            }
        ],
        "firstVariantId": "var-acc-canopy-brass"
    },
    {
        "id": "prod-knurled-brass-rotary-dimmer-switch",
        "slug": "knurled-brass-rotary-dimmer-switch",
        "handle": "knurled-brass-rotary-dimmer-switch",
        "name": "Knurled Brass Rotary Dimmer Switch",
        "productType": "Lighting Accessories",
        "category": "custom-and-accessories",
        "subcategory": "accessories",
        "price": 3600,
        "compareAtPrice": 4200,
        "description": "Solid knurled brass trailing-edge rotary dimmer plate. Silent operation, flicker-free dimming from 1% to 100% for chandeliers and LED sconces.",
        "descriptionHtml": "<p><strong>Knurled Brass Rotary Dimmer Switch</strong></p><p>Engineered from solid bar-stock brass with fine diamond knurling, this rotary dimmer switch brings a tactile, luxurious control experience to your ambient lighting.</p><p><strong>Product Highlights:</strong></p><ul><li>Finish: Hand-finished Brushed Brass plate with knurled rotary dial</li><li>Module: Smooth trailing-edge electronic dimmer (flicker-free from 1% to 100%)</li><li>Rating: 220–240V AC, 300W LED load capacity</li><li>Push on / push off with rotary smooth dimming</li><li>Fits standard Indian modular and surface mounting back-boxes</li></ul>",
        "finish": "Brushed Brass with Diamond Knurl",
        "dimensions": "86mm × 86mm × 25mm",
        "material": "Solid brass plate and dial",
        "images": [
            "/collections/knurled-dimmer-switch.jpg"
        ],
        "tags": [
            "accessories",
            "hardware",
            "dimmer",
            "custom-and-accessories"
        ],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "var-acc-dimmer-1",
                "title": "Brushed Brass Plate",
                "price": 3600,
                "availableForSale": true
            }
        ],
        "firstVariantId": "var-acc-dimmer-1"
    },
    {
        "id": "prod-k9-crystal-prism-droplets-set",
        "slug": "k9-crystal-prism-droplets-set",
        "handle": "k9-crystal-prism-droplets-set",
        "name": "Hand-Cut K9 Crystal Prism Droplets (Set of 12)",
        "productType": "Lighting Accessories",
        "category": "custom-and-accessories",
        "subcategory": "accessories",
        "price": 2900,
        "compareAtPrice": 3500,
        "description": "Set of 12 hand-faceted optical K9 crystal prism chandelier droplets with brass butterfly connecting clips. High refractive index for brilliant rainbow dispersion.",
        "descriptionHtml": "<p><strong>Hand-Cut K9 Crystal Prism Droplets (Set of 12)</strong></p><p>Authentic replacement and accent crystal prisms for luxury chandeliers and wall sconces. Crafted from pure optical-grade K9 crystal with precision faceted bevels for maximum light refraction.</p><p><strong>Product Highlights:</strong></p><ul><li>Quantity: 12 hand-cut crystal droplets per box</li><li>Length: 76 mm prism with 14 mm top octagonal bead</li><li>Fittings: Solid brass butterfly bow-tie connector pins included</li><li>Clarity: Flawless optical K9 crystal with brilliant rainbow spectrum dispersion</li><li>Protective velvet-lined packaging</li></ul>",
        "finish": "Flawless Optical Clear Crystal",
        "dimensions": "76mm prism drop + 14mm octagonal bead",
        "material": "K9 optical crystal, brass connecting pins",
        "images": [
            "/collections/crystal-prism-pack.jpg"
        ],
        "tags": [
            "accessories",
            "crystal",
            "parts",
            "custom-and-accessories"
        ],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "var-acc-prisms-12",
                "title": "Set of 12 / 76mm",
                "price": 2900,
                "availableForSale": true
            }
        ],
        "firstVariantId": "var-acc-prisms-12"
    },
    {
        "id": "gid://shopify/Product/10335980159267",
        "slug": "golden-willow-chandelier",
        "handle": "golden-willow-chandelier",
        "name": "GOLDEN WILLOW CHANDELIER",
        "productType": "modern chandelier",
        "category": "chandeliers",
        "subcategory": "modern",
        "price": 8900,
        "compareAtPrice": 0,
        "description": "Golden Willow Chandelier is a luxurious statement lighting piece inspired by the delicate beauty of blooming branches. Its sculptural golden frame is adorned with numerous sparkling crystal accents, creating a dazzling interplay of light and reflection. The organic branch-like design brings elegance, glamour, and a sophisticated contemporary touch to any interior. Perfect for living rooms, dining areas, bedrooms, luxury hotels, restaurants, and grand spaces, this chandelier transforms the ceiling into a captivating centrepiece. Product Highlights. Elegant gold-finish branch design. Premium crystal detailing. Adjustable hanging height. Compatible with G9 / E14 bulbs. Length: 60 cm. Height: 50 cm (adjustable). Base plate: 12 cm",
        "descriptionHtml": "<p>Golden Willow Chandelier is a luxurious statement lighting piece inspired by the delicate beauty of blooming branches. Its sculptural golden frame is adorned with numerous sparkling crystal accents, creating a dazzling interplay of light and reflection. The organic branch-like design brings elegance, glamour, and a sophisticated contemporary touch to any interior.</p>\n<p>Perfect for living rooms, dining areas, bedrooms, luxury hotels, restaurants, and grand spaces, this chandelier transforms the ceiling into a captivating centrepiece. </p>\n<p>Product Highlights<br>. Elegant gold-finish branch design<br>. Premium crystal detailing<br>. Adjustable hanging height<br>. Compatible with G9 / E14 bulbs<br>. Length: 60 cm<br>. Height: 50 cm (adjustable)<br>. Base plate: 12 cm</p>",
        "images": [
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-21_at_03.18.56.jpg?v=1787307929",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-21_at_03.20.10.jpg?v=1787308011",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-21_at_03.20.26.jpg?v=1787308028",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-21_at_03.20.45.jpg?v=1787308056",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_20.24.46.jpg?v=1787410806",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-21_at_17.07.23.jpg?v=1787411129",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-21_at_17.09.22.jpg?v=1787411148",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_20.34.01.jpg?v=1787411177",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-21_at_15.51.05.jpg?v=1787411432"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55493290590499",
                "title": "80CM",
                "price": 17540,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55493290623267",
                "title": "100CM",
                "price": 20950,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55493815304483",
                "title": "120CM",
                "price": 18850,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55493815337251",
                "title": "150CM",
                "price": 22180,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55493815370019",
                "title": "180CM",
                "price": 28650,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55493290590499"
    },
    {
        "id": "gid://shopify/Product/10338886025507",
        "slug": "crystal-falls-chandelier",
        "handle": "crystal-falls-chandelier",
        "name": "CRYSTAL FALLS CHANDELIER",
        "productType": "modern chandelier",
        "category": "chandeliers",
        "subcategory": "modern",
        "price": 12300,
        "compareAtPrice": 0,
        "description": "A statement of luxury and elegance, the Crystal Falls Chandelier features cascading crystal strands beautifully arranged around a sculptural golden frame. Its warm illumination and sparkling crystals create a mesmerizing waterfall effect, adding glamour and sophistication to any interior.Perfect for: Living rooms, dining areas, luxury bedrooms, hotels, and grand entrance spaces. Light source - LED Holder type- G9/E14 ( BULB INCLUDED ) Height - Adjustable",
        "descriptionHtml": "<p>A statement of luxury and elegance, the Crystal Falls Chandelier features cascading crystal strands beautifully arranged around a sculptural golden frame. Its warm illumination and sparkling crystals create a mesmerizing waterfall effect, adding glamour and sophistication to any interior.<br>Perfect for: Living rooms, dining areas, luxury bedrooms, hotels, and grand entrance spaces.</p>\n<p>Light source - LED</p>\n<p>Holder type- G9/E14 ( BULB INCLUDED )</p>\n<p>Height - Adjustable </p>",
        "images": [
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsAppImage2026-08-22at13.52.54_0c775b51-3caf-4659-b262-7363566bf521.jpg?v=1787408561",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_19.39.33.jpg?v=1787408587",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_19.52.12.jpg?v=1787408633",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_13.57.50.jpg?v=1787409325",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_13.56.12.jpg?v=1787409347",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_13.53.19.jpg?v=1787409364",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_20.04.46.jpg?v=1787409477",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-22_at_13.52.54_6e263d09-403d-4f7f-87e2-9cb84536178f.jpg?v=1787409812"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55499570938147",
                "title": "80CM",
                "price": 17900,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55499570970915",
                "title": "100CM",
                "price": 25810,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55499571003683",
                "title": "150CM",
                "price": 21900,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55499571036451",
                "title": "180CM",
                "price": 29980,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55499571069219",
                "title": "120CM",
                "price": 19650,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55499570938147"
    },
    {
        "id": "gid://shopify/Product/10342338461987",
        "slug": "smoke-clear-grande-chandelier",
        "handle": "smoke-clear-grande-chandelier",
        "name": "SMOKE & CLEAR GRANDE CHANDELIER",
        "productType": "CLASSIC DOUBLE HEIGHT CHANDELIER",
        "category": "double-height-chandeliers",
        "subcategory": "classic",
        "price": 66850,
        "compareAtPrice": 90000,
        "description": "Smoke & Clear Grande Chandelier Make a statement with the Smoke & Clear Grande Chandelier, an exquisite blend of timeless elegance and luxurious craftsmanship. Designed with multiple tiers of hand-cut smoke and clear crystals, this grand chandelier creates a captivating play of light and sparkle, adding depth and sophistication to any interior. Its striking layered silhouette and refined detailing make it an ideal centrepiece for luxury living rooms, grand entrances, dining areas, hotels, and upscale interiors. With its adjustable height, the chandelier can be tailored perfectly to your space. Specifications - Diameter: 120 cm- Height: 180 cm (Adjustable)- Bulb: E14- Crystal: Smoke & Clear- Design: Multi-tiered grand chandelier- Finish: Premium luxury detailing",
        "descriptionHtml": "<p>Smoke &amp; Clear Grande Chandelier</p>\n<p>Make a statement with the Smoke &amp; Clear Grande Chandelier, an exquisite blend of timeless elegance and luxurious craftsmanship. Designed with multiple tiers of hand-cut smoke and clear crystals, this grand chandelier creates a captivating play of light and sparkle, adding depth and sophistication to any interior.</p>\n<p>Its striking layered silhouette and refined detailing make it an ideal centrepiece for luxury living rooms, grand entrances, dining areas, hotels, and upscale interiors. With its adjustable height, the chandelier can be tailored perfectly to your space.</p>\n<p>Specifications</p>\n<p>- Diameter: 120 cm<br>- Height: 180 cm (Adjustable)<br>- Bulb: E14<br>- Crystal: Smoke &amp; Clear<br>- Design: Multi-tiered grand chandelier<br>- Finish: Premium luxury detailing</p>",
        "images": [
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsAppImage2026-08-25at15.04.19.jpg?v=1787653447",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_16.07.29.jpg?v=1787654441",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_16.26.15.jpg?v=1787655424"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55510843523363",
                "title": "Default Title",
                "price": 66850,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55510843523363"
    },
    {
        "id": "gid://shopify/Product/10342354354467",
        "slug": "aurora-spiral-chandelier",
        "handle": "aurora-spiral-chandelier",
        "name": "AURORA SPIRAL CHANDELIER",
        "productType": "MODERN DOUBLE HEIGHT CHANDELIER",
        "category": "double-height-chandeliers",
        "subcategory": "modern",
        "price": 154310,
        "compareAtPrice": 0,
        "description": "Aurora Spiral Chandelier is a striking statement piece designed to transform double-height spaces with its graceful flowing spiral silhouette. Featuring 238 elegant glass elements in white and gold, the chandelier creates a captivating sense of movement while delivering a luxurious and contemporary aesthetic. Its adjustable suspension allows the complete fixture height to be customized from 4000 mm to 5600 mm, making it ideal for grand staircases, double-height foyers, luxury residences, hotels, and sophisticated commercial interiors. Specifications Plate Size: 1000 mm Plate Colour - Gold Fixture Height: 4000 mm Additional Wire: 1600 mm Adjustable Overall Height: 4000–5600 mm No. of Glasses: 238 Light of Source - in built LED Finish: White & Gold Glass",
        "descriptionHtml": "<p>Aurora Spiral Chandelier is a striking statement piece designed to transform double-height spaces with its graceful flowing spiral silhouette. Featuring 238 elegant glass elements in white and gold, the chandelier creates a captivating sense of movement while delivering a luxurious and contemporary aesthetic. Its adjustable suspension allows the complete fixture height to be customized from 4000 mm to 5600 mm, making it ideal for grand staircases, double-height foyers, luxury residences, hotels, and sophisticated commercial interiors.</p>\n<p>Specifications</p>\n<p>Plate Size: 1000 mm </p>\n<p>Plate Colour - Gold</p>\n<p>Fixture Height: 4000 mm</p>\n<p>Additional Wire: 1600 mm</p>\n<p>Adjustable Overall Height: 4000–5600 mm</p>\n<p>No. of Glasses: 238</p>\n<p>Light of Source - in built LED</p>\n<p>Finish: White &amp; Gold Glass</p>",
        "images": [
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsAppImage2026-08-24at22.38.38.jpg?v=1787657770",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_17.35.33.jpg?v=1787659594",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_17.36.42.jpg?v=1787659633"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55510915776803",
                "title": "Default Title",
                "price": 154310,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55510915776803"
    },
    {
        "id": "gid://shopify/Product/10342471926051",
        "slug": "22-hanging-butterfly-chandelier",
        "handle": "22-hanging-butterfly-chandelier",
        "name": "22 HANGING BUTTERFLY CHANDELIER",
        "productType": "MODERN DOUBLE HEIGHT CHANDELIER",
        "category": "double-height-chandeliers",
        "subcategory": "modern",
        "price": 35900,
        "compareAtPrice": 0,
        "description": "22-Hanging Butterfly Chandelier Make a bold statement with this elegant 22-Hanging Chandelier, designed with a graceful cascading arrangement of 22 illuminated elements. Its contemporary form adds a luxurious and artistic touch to double-height spaces while creating a beautiful floating-light effect. Featuring built-in Tricolour LED lighting, the chandelier allows you to create different moods with warm, cool, and neutral illumination. The adjustable 300 cm height provides flexibility for different ceiling heights and interior requirements. Specifications - Diameter: 60 cm- Height: 300 cm (Adjustable)- Light: Built-in LED- Lighting: Tricolour- Design: 22 Hanging Elements- Ideal For: Living rooms, staircases, foyers, luxury residences, hotels, and double-height spaces A perfect blend of modern elegance, artistic design, and versatile illumination, this chandelier becomes an instant focal point in any sophisticated interior.",
        "descriptionHtml": "<p>22-Hanging Butterfly Chandelier</p>\n<p>Make a bold statement with this elegant 22-Hanging Chandelier, designed with a graceful cascading arrangement of 22 illuminated elements. Its contemporary form adds a luxurious and artistic touch to double-height spaces while creating a beautiful floating-light effect.</p>\n<p>Featuring built-in Tricolour LED lighting, the chandelier allows you to create different moods with warm, cool, and neutral illumination. The adjustable 300 cm height provides flexibility for different ceiling heights and interior requirements.</p>\n<p>Specifications</p>\n<p>- Diameter: 60 cm<br>- Height: 300 cm (Adjustable)<br>- Light: Built-in LED<br>- Lighting: Tricolour<br>- Design: 22 Hanging Elements<br>- Ideal For: Living rooms, staircases, foyers, luxury residences, hotels, and double-height spaces</p>\n<p>A perfect blend of modern elegance, artistic design, and versatile illumination, this chandelier becomes an instant focal point in any sophisticated interior.</p>",
        "images": [
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsAppImage2026-08-25at19.16.19.jpg?v=1787666159",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_19.16.49.jpg?v=1787666176",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_19.17.02.jpg?v=1787666197",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_19.17.15.jpg?v=1787666215"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55511321411875",
                "title": "Default Title",
                "price": 35900,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55511321411875"
    },
    {
        "id": "gid://shopify/Product/10342671941923",
        "slug": "lily-blossom-chandelier",
        "handle": "lily-blossom-chandelier",
        "name": "LILY BLOSSOM CHANDELIER",
        "productType": "MODERN DOUBLE HEIGHT CHANDELIER",
        "category": "double-height-chandeliers",
        "subcategory": "modern",
        "price": 154450,
        "compareAtPrice": 0,
        "description": "Lily Blossom Chandelier Make a dramatic statement with this exquisite ceramic chandelier, inspired by the delicate beauty of birds, nests, and organic forms. Designed as a spectacular vertical installation, the chandelier features 203 ceramic nest elements and 10 ceramic birds, creating a captivating cascading composition. Each ceramic element is individually suspended to form a luxurious, layered silhouette, while warm illumination from G9 bulbs enhances the texture, depth, and sculptural beauty of the design. The combination of soft white ceramic and elegant detailing makes this chandelier an exceptional centerpiece for grand residences, hotels, restaurants, double-height spaces, and luxury interiors. Specifications Diameter: 1000 mm Height: 11 ft, adjustable suspension Ceramic Nests: 203 pieces Ceramic Birds: 10 pieces Light Source: G9 bulbs Material: ceramic with decorative suspension structure Installation: Suspended ceiling chandelier Height: Adjustable to suit the installation space A striking fusion of art, craftsmanship, and illumination, the Ceramic Nest & Bird Chandelier transforms any interior into an unforgettable visual experience",
        "descriptionHtml": "<p>Lily Blossom Chandelier </p>\n<p class=\"isSelectedEnd\"><span>Make a dramatic statement with this exquisite </span><strong><span>ceramic chandelier</span></strong><span>, inspired by the delicate beauty of birds, nests, and organic forms. Designed as a spectacular vertical installation, the chandelier features </span><strong><span>203 ceramic nest elements</span></strong><span> and </span><strong><span>10 ceramic birds</span></strong><span>, creating a captivating cascading composition.</span></p>\n<p class=\"isSelectedEnd\"><span>Each ceramic element is individually suspended to form a luxurious, layered silhouette, while warm illumination from </span><strong><span>G9 bulbs</span></strong><span> enhances the texture, depth, and sculptural beauty of the design. The combination of soft white ceramic and elegant detailing makes this chandelier an exceptional centerpiece for grand residences, hotels, restaurants, double-height spaces, and luxury interiors.</span></p>\n<h3><span>Specifications</span></h3>\n<ul>\n<li>\n<strong><span>Diameter:</span></strong><span> 1000 mm</span>\n</li>\n<li>\n<strong><span>Height:</span></strong><span> 11 ft, adjustable suspension</span>\n</li>\n<li>\n<strong><span>Ceramic Nests:</span></strong><span> 203 pieces</span>\n</li>\n<li>\n<strong><span>Ceramic Birds:</span></strong><span> 10 pieces</span>\n</li>\n<li>\n<strong><span>Light Source:</span></strong><span> G9 bulbs</span>\n</li>\n<li>\n<strong><span>Material:</span></strong><span> ceramic with decorative suspension structure</span>\n</li>\n<li>\n<strong><span>Installation:</span></strong><span> Suspended ceiling chandelier</span>\n</li>\n<li>\n<strong><span>Height:</span></strong><span> Adjustable to suit the installation space</span>\n</li>\n</ul>\n<p><span>A striking fusion of </span><strong><span>art, craftsmanship, and illumination</span></strong><span>, the Ceramic Nest &amp; Bird Chandelier transforms any interior into an unforgettable visual experience</span></p>",
        "images": [
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsAppImage2026-08-25at20.33.36.jpg?v=1787671707",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_20.39.51.jpg?v=1787671734",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_19.44.55_3.jpg?v=1787671810",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_19.44.55_4.jpg?v=1787671832",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/ChatGPT_Image_Aug_25_2026_08_52_24_PM.png?v=1787671917"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55511878041891",
                "title": "Default Title",
                "price": 154450,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55511878041891"
    },
    {
        "id": "gid://shopify/Product/10342764478755",
        "slug": "tri-tier-crystal-waterfall-chandelier",
        "handle": "tri-tier-crystal-waterfall-chandelier",
        "name": "TRI-TIER CRYSTAL WATERFALL CHANDELIER",
        "productType": "CLASSIC DOUBLE HEIGHT CHANDELIER",
        "category": "double-height-chandeliers",
        "subcategory": "classic",
        "price": 100900,
        "compareAtPrice": 0,
        "description": "Luxury Crystal Chandelier Elevate your interior with this stunning Luxury Crystal Chandelier, designed to create a dramatic statement in grand and sophisticated spaces. Featuring cascading crystal elements arranged in an elegant multi-level composition, this chandelier reflects and refracts light beautifully, creating a luxurious sparkling effect. Its impressive vertical design makes it an ideal centrepiece for double-height ceilings, hotel lobbies, villas, banquet halls, luxury residences, and premium commercial interiors. The combination of crystal detailing and a refined metal structure delivers timeless elegance with a contemporary architectural presence. Specifications Diameter: 1200 mm Height: 3000 mm Bulb Type: E14 Style: Multi-tier Luxury Crystal Chandelier Installation: Ceiling Suspended A perfect blend of luxury, brilliance, and contemporary design, this chandelier transforms any space into an elegant and unforgettable setting.",
        "descriptionHtml": "<h2>Luxury Crystal Chandelier</h2>\n<p>Elevate your interior with this stunning <strong>Luxury Crystal Chandelier</strong>, designed to create a dramatic statement in grand and sophisticated spaces. Featuring cascading crystal elements arranged in an elegant multi-level composition, this chandelier reflects and refracts light beautifully, creating a luxurious sparkling effect.</p>\n<p>Its impressive vertical design makes it an ideal centrepiece for <strong>double-height ceilings, hotel lobbies, villas, banquet halls, luxury residences, and premium commercial interiors</strong>. The combination of crystal detailing and a refined metal structure delivers timeless elegance with a contemporary architectural presence.</p>\n<h3>Specifications</h3>\n<ul>\n<li>\n<p><strong>Diameter:</strong> 1200 mm</p>\n</li>\n<li>\n<p><strong>Height:</strong> 3000 mm</p>\n</li>\n<li>\n<p><strong>Bulb Type:</strong> E14</p>\n</li>\n<li>\n<p><strong>Style:</strong> Multi-tier Luxury Crystal Chandelier</p>\n</li>\n<li>\n<p><strong>Installation:</strong> Ceiling Suspended</p>\n</li>\n</ul>\n<p>A perfect blend of <strong>luxury, brilliance, and contemporary design</strong>, this chandelier transforms any space into an elegant and unforgettable setting.</p>",
        "images": [
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsAppImage2026-08-24at22.38.30.jpg?v=1787677519",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_22.34.08.jpg?v=1787677629",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_22.32.54.jpg?v=1787677646",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_22.11.59.jpg?v=1787677682",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_22.09.47_6b6e8f76-2d34-4e43-a89f-0099647b0430.jpg?v=1787677725",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_22.08.51_2aee5656-e1c5-4d02-b6ff-40f129a88a92.jpg?v=1787677751",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-25_at_22.13.10.jpg?v=1787677666",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/ChatGPT_Image_Aug_25_2026_09_48_37_PM.png?v=1787677827"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55512143626531",
                "title": "Default Title",
                "price": 100900,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55512143626531"
    },
    {
        "id": "gid://shopify/Product/10344229667107",
        "slug": "draped-crystal-chain-chandelier",
        "handle": "draped-crystal-chain-chandelier",
        "name": "DRAPED CRYSTAL CHAIN CHANDELIER",
        "productType": "MODERN DOUBLE HEIGHT CHANDELIER",
        "category": "double-height-chandeliers",
        "subcategory": "modern",
        "price": 109900,
        "compareAtPrice": 0,
        "description": "Draped Crystal Chain Chandelier Elevate your interiors with the Draped Crystal Chain Chandelier, a statement lighting piece designed with graceful cascading crystal loops and a luxurious contemporary finish. Crafted with clear crystals, it creates a dazzling play of light while adding an elegant, sophisticated touch to grand living spaces, hotel lobbies, stairwells, and luxury interiors. Product Details Diameter: 1000 mm Height: 2600 mm Crystal: Clear Crystal Light Source: In-built LED Light Colour: Tricolour Design: Cascading Loop Style: Luxury Contemporary A perfect blend of elegance, brilliance, and modern luxury, the Aurelia Cascade makes a striking centrepiece for any premium interior.",
        "descriptionHtml": "<p>Draped Crystal Chain Chandelier</p>\n<p> </p>\n<p>Elevate your interiors with the Draped Crystal Chain Chandelier, a statement lighting piece designed with graceful cascading crystal loops and a luxurious contemporary finish. Crafted with clear crystals, it creates a dazzling play of light while adding an elegant, sophisticated touch to grand living spaces, hotel lobbies, stairwells, and luxury interiors.</p>\n<p> </p>\n<p>Product Details</p>\n<p>Diameter: 1000 mm</p>\n<p>Height: 2600 mm</p>\n<p>Crystal: Clear Crystal</p>\n<p>Light Source: In-built LED</p>\n<p>Light Colour: Tricolour</p>\n<p>Design: Cascading Loop</p>\n<p>Style: Luxury Contemporary</p>\n<p><br>A perfect blend of elegance, brilliance, and modern luxury, the Aurelia Cascade makes a striking centrepiece for any premium interior.</p>",
        "images": [
            "/collections/draped-crystal-chain-chandelier.jpg"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55516176777507",
                "title": "Default Title",
                "price": 109900,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55516176777507"
    },
    {
        "id": "gid://shopify/Product/10351879258403",
        "slug": "dual",
        "handle": "dual",
        "name": "TRUMPET WALL LAMP",
        "productType": "CLASSIC / MORDEN WALL LIGHT",
        "category": "wall",
        "subcategory": "classic",
        "price": 2610,
        "compareAtPrice": 0,
        "description": "Dual and Single Trumpet Wall Light Add a touch of timeless luxury to your interiors with the Dual and Single Trumpet Wall Light. Featuring a sophisticated black shade paired with a warm brass -finished metal body, this two-light wall light & single wall light creates an elegant and inviting glow. Its sculptural design blends classic detailing with a modern aesthetic, making it perfect for bedrooms, living rooms, hallways, hotels, and luxury interiors. Key Features: Elegant black & brass finish Dual-light and Single-light design Warm, ambient illumination Premium decorative look Ideal for modern and luxury interiors Metrial - Metal and Febric Holder -E14",
        "descriptionHtml": "<p>Dual and Single Trumpet Wall Light Add a touch of timeless luxury to your interiors with the Dual and Single Trumpet Wall Light. Featuring a sophisticated black shade paired with a warm brass -finished metal body, this two-light wall light &amp; single wall light creates an elegant and inviting glow. Its sculptural design blends classic detailing with a modern aesthetic, making it perfect for bedrooms, living rooms, hallways, hotels, and luxury interiors.</p>\n<p>Key Features:</p>\n<p>Elegant black &amp; brass finish</p>\n<p>Dual-light and Single-light design</p>\n<p>Warm, ambient illumination</p>\n<p>Premium decorative look</p>\n<p>Ideal for modern and luxury interiors</p>\n<p>Metrial - Metal and Febric</p>\n<p>Holder -E14</p>",
        "images": [
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsAppImage2026-08-30at15.59.29.jpg?v=1788175077",
            "https://cdn.shopify.com/s/files/1/0993/1961/1683/files/WhatsApp_Image_2026-08-30_at_15.59.29_1.jpg?v=1788175494"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55539348963619",
                "title": "DUAL TRUMPET",
                "price": 3990,
                "availableForSale": true
            },
            {
                "id": "gid://shopify/ProductVariant/55539348996387",
                "title": "SINGLE TRUMPET",
                "price": 2610,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55539348963619"
    },
    {
        "id": "gid://shopify/Product/10351996862755",
        "slug": "twisted-crystal-floor-lamp",
        "handle": "twisted-crystal-floor-lamp",
        "name": "TWISTED CRYSTAL FLOOR LAMP",
        "productType": "FLOOR LAMP",
        "category": "floor",
        "subcategory": "",
        "price": 11990,
        "compareAtPrice": 19550,
        "description": "TWISTED CRYSTAL TWIST FLOOR LAMP Add a touch of timeless elegance to your interiors with the Crystal Twist Floor Lamp. Featuring a beautifully pleated fabric shade, a clear twisted crystal-style column, and a polished brass -finish metal frame, this floor lamp creates a warm and sophisticated ambience. Its classic silhouette blends effortlessly with modern, contemporary, and luxury interiors—perfect for living rooms, bedrooms, lounges, and premium hospitality spaces. Highlights: Elegant pleated fabric lampshade Crystal-inspired twisted centre column Premium brass finish Warm, sophisticated illumination Sturdy stepped round base Ideal for luxury residential and commercial interiors",
        "descriptionHtml": "<p>TWISTED CRYSTAL TWIST FLOOR LAMP</p>\n<p>Add a touch of timeless elegance to your interiors with the Crystal Twist Floor Lamp. Featuring a beautifully pleated fabric shade, a clear twisted crystal-style column, and a polished brass -finish metal frame, this floor lamp creates a warm and sophisticated ambience. Its classic silhouette blends effortlessly with modern, contemporary, and luxury interiors—perfect for living rooms, bedrooms, lounges, and premium hospitality spaces.</p>\n<p><strong>Highlights:</strong></p>\n<p>Elegant pleated fabric lampshade</p>\n<p>Crystal-inspired twisted centre column</p>\n<p>Premium brass finish</p>\n<p>Warm, sophisticated illumination</p>\n<p>Sturdy stepped round base</p>\n<p>Ideal for luxury residential and commercial interiors</p>",
        "images": [
            "/collections/twisted-crystal-floor-lamp.jpg"
        ],
        "tags": [],
        "featured": true,
        "new_arrival": false,
        "availableForSale": true,
        "variants": [
            {
                "id": "gid://shopify/ProductVariant/55539554713891",
                "title": "Default Title",
                "price": 11990,
                "availableForSale": true
            }
        ],
        "firstVariantId": "gid://shopify/ProductVariant/55539554713891"
    }
];

function filterFallbackProducts({ query = "", first = 100 } = {}) {
    let prods = [...FALLBACK_PRODUCTS];
    if (query && query.trim()) {
        const q = query.trim().toLowerCase();
        prods = prods.filter((p) =>
            p.name.toLowerCase().includes(q) ||
            p.description.toLowerCase().includes(q) ||
            p.category.toLowerCase().includes(q) ||
            (p.tags || []).some((t) => t.toLowerCase().includes(q))
        );
    }
    return prods.slice(0, first);
}

function getFallbackProductByHandle(handle) {
    if (!handle) return null;
    const h = handle.toLowerCase();
    return FALLBACK_PRODUCTS.find((p) => p.slug.toLowerCase() === h || p.handle.toLowerCase() === h) || null;
}

export async function listProducts({ first = 100, query = "" } = {}) {
    let live = [];
    if (isShopifyConfigured()) {
        try {
            const data = await storefront(Q_PRODUCTS, { first, query });
            live = (data.products?.nodes || []).map(normalizeProduct).filter(Boolean);
        } catch (e) {
            console.warn("Shopify Storefront API query failed, displaying Adore catalog:", e.message);
        }
    }
    const fallback = filterFallbackProducts({ first, query });
    if (live.length === 0) return fallback;

    // Combine live Shopify products with catalog items (like custom & accessories) so new categories are populated
    const liveHandles = new Set(live.map((p) => (p.handle || p.slug || "").toLowerCase()));
    const extraFallback = fallback.filter((p) => !liveHandles.has((p.handle || p.slug || "").toLowerCase()));
    return [...live, ...extraFallback];
}

export async function getProductByHandle(handle) {
    if (isShopifyConfigured()) {
        try {
            const data = await storefront(Q_PRODUCT_BY_HANDLE, { handle });
            if (data?.product) return normalizeProduct(data.product);
        } catch (e) {
            console.warn("Shopify getProductByHandle failed, searching catalog:", e.message);
        }
    }
    return getFallbackProductByHandle(handle);
}

// Cart normalization & Local Cart support
function forceShopifyHost(url) {
    if (!url || !DOMAIN) return url;
    try {
        const u = new URL(url);
        if (u.hostname === "adore.lighting" || !u.hostname.endsWith(".myshopify.com")) {
            u.hostname = DOMAIN;
            u.protocol = "https:";
        }
        if (!u.searchParams.has("_fd")) {
            u.searchParams.set("_fd", "0");
        }
        return u.toString();
    } catch {
        return url;
    }
}

export function normalizeCart(cart) {
    if (!cart) return null;
    return {
        id: cart.id,
        checkoutUrl: forceShopifyHost(cart.checkoutUrl),
        totalQuantity: cart.totalQuantity || 0,
        subtotal: Math.round(Number(cart.cost?.subtotalAmount?.amount || 0)),
        total: Math.round(Number(cart.cost?.totalAmount?.amount || 0)),
        currency: cart.cost?.totalAmount?.currencyCode || "INR",
        lines: (cart.lines?.nodes || []).map((l) => ({
            lineId: l.id,
            variantId: l.merchandise.id,
            variantTitle: l.merchandise.title,
            productId: l.merchandise.product?.id,
            productHandle: l.merchandise.product?.handle,
            name: l.merchandise.product?.title || l.merchandise.title,
            image: l.merchandise.image?.url || "",
            price: Math.round(Number(l.merchandise.price.amount)),
            quantity: l.quantity,
        })),
    };
}

const LOCAL_STORAGE_CART_KEY = "adore_local_cart_store";

function getStoredLocalCart() {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_CART_KEY);
        if (raw) return JSON.parse(raw);
    } catch {}
    return null;
}

function saveStoredLocalCart(cart) {
    try {
        if (cart) localStorage.setItem(LOCAL_STORAGE_CART_KEY, JSON.stringify(cart));
        else localStorage.removeItem(LOCAL_STORAGE_CART_KEY);
    } catch {}
}

function buildLocalCartState(lines = []) {
    let subtotal = 0;
    let totalQty = 0;
    const formattedLines = lines.map((l) => {
        subtotal += (l.price || 0) * (l.quantity || 1);
        totalQty += (l.quantity || 1);
        return l;
    });

    const itemsSummary = formattedLines.map((i) => `${i.name} (x${i.quantity})`).join(", ");
    const waUrl = `https://wa.me/918178867115?text=${encodeURIComponent(`Hi Adore Lighting, I would like to place an order for: ${itemsSummary}`)}`;
    const shopifyDirectUrl = DOMAIN ? `https://${DOMAIN}/cart` : waUrl;

    return {
        id: "adore_local_cart",
        checkoutUrl: "",
        totalQuantity: totalQty,
        subtotal,
        total: subtotal,
        currency: "INR",
        lines: formattedLines,
    };
}

function localCartCreate(items = []) {
    const lines = items.map((item, idx) => {
        const fallbackProd = FALLBACK_PRODUCTS.find((p) =>
            p.firstVariantId === item.merchandiseId ||
            p.variants?.some((v) => v.id === item.merchandiseId)
        ) || FALLBACK_PRODUCTS[0];

        return {
            lineId: `line-${Date.now()}-${idx}`,
            variantId: item.merchandiseId || fallbackProd.firstVariantId,
            variantTitle: fallbackProd.name,
            productId: fallbackProd.id,
            productHandle: fallbackProd.slug,
            name: fallbackProd.name,
            image: fallbackProd.images?.[0] || "",
            price: fallbackProd.price,
            quantity: item.quantity || 1,
        };
    });

    const cart = buildLocalCartState(lines);
    saveStoredLocalCart(cart);
    return cart;
}

const CART_FIELDS = `
    id
    checkoutUrl
    totalQuantity
    cost {
        subtotalAmount { amount currencyCode }
        totalAmount { amount currencyCode }
    }
    lines(first: 100) {
        nodes {
            id
            quantity
            merchandise {
                ... on ProductVariant {
                    id
                    title
                    price { amount currencyCode }
                    image { url altText }
                    product { id title handle }
                }
            }
        }
    }
`;

const M_CART_CREATE = `
    mutation CartCreate($lines: [CartLineInput!]) {
        cartCreate(input: { lines: $lines }) {
            cart { ${CART_FIELDS} }
            userErrors { message field }
        }
    }
`;
const M_LINES_ADD = `
    mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart { ${CART_FIELDS} }
            userErrors { message field }
        }
    }
`;
const M_LINES_UPDATE = `
    mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart { ${CART_FIELDS} }
            userErrors { message field }
        }
    }
`;
const M_LINES_REMOVE = `
    mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart { ${CART_FIELDS} }
            userErrors { message field }
        }
    }
`;
const Q_CART = `
    query Cart($id: ID!) { cart(id: $id) { ${CART_FIELDS} } }
`;

const throwUserErrors = (mutationRoot) => {
    const errs = mutationRoot?.userErrors || [];
    if (errs.length) throw new Error(errs.map((e) => e.message).join("; "));
    return mutationRoot?.cart || null;
};

const DEFAULT_SHOPIFY_VARIANT = "gid://shopify/ProductVariant/55493290590499";

function toShopifyLineInput(item) {
    if (!item) return null;
    const isGid = typeof item.merchandiseId === "string" && item.merchandiseId.startsWith("gid://shopify/ProductVariant/");
    if (isGid) {
        return {
            merchandiseId: item.merchandiseId,
            quantity: item.quantity || 1,
            attributes: item.attributes || [],
        };
    }
    const fallbackProd = FALLBACK_PRODUCTS.find((p) =>
        p.firstVariantId === item.merchandiseId ||
        p.variants?.some((v) => v.id === item.merchandiseId)
    ) || FALLBACK_PRODUCTS[0];

    return {
        merchandiseId: DEFAULT_SHOPIFY_VARIANT,
        quantity: item.quantity || 1,
        attributes: [
            { key: "Catalogue Selection", value: fallbackProd.name || "Custom Design" },
            { key: "Catalogue Code", value: String(item.merchandiseId || fallbackProd.slug) },
        ],
    };
}

export async function cartCreate(lines = []) {
    if (isShopifyConfigured()) {
        const shopifyLines = lines.map(toShopifyLineInput).filter(Boolean);
        if (shopifyLines.length > 0) {
            try {
                const data = await storefront(M_CART_CREATE, { lines: shopifyLines });
                const cart = throwUserErrors(data.cartCreate);
                if (cart) return normalizeCart(cart);
            } catch (e) {
                console.warn("Shopify cartCreate error, using local bag:", e.message);
            }
        }
    }
    return localCartCreate(lines);
}

export async function cartLinesAdd(cartId, lines) {
    if (isShopifyConfigured() && !cartId?.startsWith("adore_local")) {
        const shopifyLines = lines.map(toShopifyLineInput).filter(Boolean);
        if (shopifyLines.length > 0) {
            try {
                const data = await storefront(M_LINES_ADD, { cartId, lines: shopifyLines });
                const cart = throwUserErrors(data.cartLinesAdd);
                if (cart) return normalizeCart(cart);
            } catch (e) {
                console.warn("Shopify cartLinesAdd error:", e.message);
            }
        }
    }
    const current = getStoredLocalCart() || buildLocalCartState();
    const existing = [...current.lines];
    for (const item of lines) {
        const idx = existing.findIndex((l) => l.variantId === item.merchandiseId);
        if (idx >= 0) {
            existing[idx].quantity += (item.quantity || 1);
        } else {
            const prod = FALLBACK_PRODUCTS.find((p) =>
                p.firstVariantId === item.merchandiseId ||
                p.variants.some((v) => v.id === item.merchandiseId)
            ) || FALLBACK_PRODUCTS[0];
            existing.push({
                lineId: `line-${Date.now()}-${existing.length}`,
                variantId: item.merchandiseId || prod.firstVariantId,
                variantTitle: prod.name,
                productId: prod.id,
                productHandle: prod.slug,
                name: prod.name,
                image: prod.images?.[0] || "",
                price: prod.price,
                quantity: item.quantity || 1,
            });
        }
    }
    const next = buildLocalCartState(existing);
    saveStoredLocalCart(next);
    return next;
}

export async function cartLinesUpdate(cartId, lines) {
    if (isShopifyConfigured() && !cartId?.startsWith("adore_local")) {
        try {
            const data = await storefront(M_LINES_UPDATE, { cartId, lines });
            return normalizeCart(throwUserErrors(data.cartLinesUpdate));
        } catch (e) {
            console.warn("Shopify cartLinesUpdate error:", e.message);
        }
    }
    const current = getStoredLocalCart() || buildLocalCartState();
    const updated = current.lines.map((l) => {
        const match = lines.find((u) => u.id === l.lineId);
        return match ? { ...l, quantity: match.quantity } : l;
    });
    const next = buildLocalCartState(updated);
    saveStoredLocalCart(next);
    return next;
}

export async function cartLinesRemove(cartId, lineIds) {
    if (isShopifyConfigured() && !cartId?.startsWith("adore_local")) {
        try {
            const data = await storefront(M_LINES_REMOVE, { cartId, lineIds });
            return normalizeCart(throwUserErrors(data.cartLinesRemove));
        } catch (e) {
            console.warn("Shopify cartLinesRemove error:", e.message);
        }
    }
    const current = getStoredLocalCart() || buildLocalCartState();
    const filtered = current.lines.filter((l) => !lineIds.includes(l.lineId));
    const next = buildLocalCartState(filtered);
    saveStoredLocalCart(next);
    return next;
}

export async function getCart(cartId) {
    if (isShopifyConfigured() && !cartId?.startsWith("adore_local")) {
        try {
            const data = await storefront(Q_CART, { id: cartId });
            return normalizeCart(data.cart);
        } catch (e) {
            console.warn("Shopify getCart error:", e.message);
        }
    }
    return getStoredLocalCart() || buildLocalCartState();
}
